export interface AreaGuide {
  slug: string;
  title: string;
  tagline: string;
  overview: string[];
  highlights: string[];
  transport: string[];
  rentalSnapshot: string;
  keywords: string[];
}

export const AREA_GUIDES: AreaGuide[] = [
  {
    slug: "north-london",
    title: "North London",
    tagline: "Hampstead, Islington & Camden",
    overview: [
      "North London blends village character with excellent connectivity. Hampstead and Highgate offer leafy streets and period architecture, while Islington and Camden bring vibrant dining, markets, and cultural venues.",
      "Popular with professionals and families seeking space without sacrificing access to the West End. Rental stock ranges from converted Victorian mansions to contemporary apartments near King's Cross and Angel.",
    ],
    highlights: [
      "Hampstead Heath",
      "Camden Market",
      "Islington dining scene",
      "King's Cross regeneration",
      "Victorian conversions",
      "Family-friendly pockets",
    ],
    transport: [
      "Northern Line",
      "Victoria Line",
      "Piccadilly Line",
      "London Overground",
    ],
    rentalSnapshot:
      "Strong demand from professionals and families. Mix of period flats, mansion conversions, and new-build apartments near major transport hubs.",
    keywords: [
      "hampstead",
      "islington",
      "camden",
      "highgate",
      "archway",
      "holloway",
      "finsbury",
      "barnet",
      "enfield",
    ],
  },
  {
    slug: "central-london",
    title: "Central London",
    tagline: "Mayfair, Soho & The City",
    overview: [
      "The heart of the capital — world-class dining, theatres, galleries, and business districts within walking distance. Mayfair and Marylebone offer prestigious addresses; Soho and Fitzrovia attract creative professionals.",
      "Underground connections are exceptional across the Central, Jubilee, Piccadilly, and Elizabeth lines. Many buildings offer concierge services, gyms, and premium amenities.",
    ],
    highlights: [
      "Mayfair & Marylebone",
      "Soho nightlife",
      "The City",
      "West End theatres",
      "Concierge buildings",
      "Premium amenities",
    ],
    transport: [
      "Central Line",
      "Jubilee Line",
      "Piccadilly Line",
      "Elizabeth Line",
      "Circle & District Lines",
    ],
    rentalSnapshot:
      "Luxury apartments and serviced residences dominate. Tenants include finance professionals, diplomats, and international relocations seeking prime central addresses.",
    keywords: [
      "mayfair",
      "soho",
      "city of london",
      "westminster",
      "covent garden",
      "holborn",
      "bloomsbury",
      "fitzrovia",
      "marylebone",
      "ec1",
      "ec2",
      "ec3",
      "ec4",
      "wc1",
      "wc2",
    ],
  },
  {
    slug: "east-london",
    title: "East London",
    tagline: "Shoreditch, Hackney & Docklands",
    overview: [
      "East London has transformed into one of the capital's most dynamic rental markets. Shoreditch and Hackney attract creatives and tech professionals, while Canary Wharf and Docklands offer riverside living with modern towers.",
      "The Elizabeth line, DLR, and Overground provide fast links to the City and West End. Expect warehouse conversions, new-build apartments with amenities, and growing family-friendly pockets.",
    ],
    highlights: [
      "Shoreditch creative scene",
      "Canary Wharf",
      "Victoria Park",
      "Street food markets",
      "Warehouse lofts",
      "Riverside towers",
    ],
    transport: [
      "Elizabeth Line",
      "DLR",
      "London Overground",
      "Central Line",
      "Jubilee Line",
    ],
    rentalSnapshot:
      "Young professionals and tech workers drive demand. Mix of industrial conversions, amenity-rich new builds, and emerging family neighbourhoods.",
    keywords: [
      "shoreditch",
      "hackney",
      "docklands",
      "canary wharf",
      "bethnal green",
      "stratford",
      "bow",
      "mile end",
      "whitechapel",
      "tower hamlets",
      "wapping",
      "e1",
      "e2",
      "e3",
      "e14",
    ],
  },
  {
    slug: "west-london",
    title: "West London",
    tagline: "Kensington, Chelsea & Notting Hill",
    overview: [
      "Classic London elegance — white stucco terraces, garden squares, and boutique shopping on Kensington High Street and King's Road. Notting Hill's colourful streets and Portobello Market remain iconic.",
      "Circle, District, and Central line stations connect West London to the rest of the city. Hyde Park and Holland Park offer green respite in the urban landscape.",
    ],
    highlights: [
      "Notting Hill",
      "King's Road",
      "Holland Park",
      "Kensington Gardens",
      "Garden squares",
      "Prestigious schools",
    ],
    transport: [
      "Circle Line",
      "District Line",
      "Central Line",
      "Hammersmith & City Line",
    ],
    rentalSnapshot:
      "Grand period flats, mews houses, and luxury developments. Appeals to families, executives, and tenants seeking established West London character.",
    keywords: [
      "kensington",
      "chelsea",
      "notting hill",
      "hammersmith",
      "fulham",
      "chiswick",
      "shepherd's bush",
      "paddington",
      "bayswater",
      "w8",
      "w10",
      "w11",
      "w14",
      "sw3",
      "sw5",
      "sw6",
      "sw10",
    ],
  },
];

export function getAreaGuideBySlug(slug: string): AreaGuide | undefined {
  return AREA_GUIDES.find((g) => g.slug === slug);
}

export function matchAreaGuideRegion(
  postcode: string,
  displayAddress: string
): AreaGuide | null {
  const haystack = `${postcode} ${displayAddress}`.toLowerCase();
  for (const guide of AREA_GUIDES) {
    if (guide.keywords.some((kw) => haystack.includes(kw))) {
      return guide;
    }
  }
  return null;
}
